import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, Phone, X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface SOSButtonProps {
  onTrigger: () => void;
  isActive: boolean;
}

export function SOSButton({ onTrigger, isActive }: SOSButtonProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (isActive) return null;

  return (
    <>
      {/* Inline SOS Button for header */}
      <motion.button
        onClick={() => setConfirmOpen(true)}
        className="relative w-7 h-7 rounded-full bg-destructive/90 text-destructive-foreground shadow-sm flex items-center justify-center"
        whileTap={{ scale: 0.9 }}
        title="SOS Emergencia"
      >
        <AlertTriangle className="w-3.5 h-3.5" />
        
        {/* Active pulse indicator */}
        <motion.div
          className="absolute inset-0 rounded-full border border-destructive/50"
          animate={{
            scale: [1, 1.5],
            opacity: [0.4, 0],
          }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </motion.button>

      {/* Confirmation Dialog */}
      <AnimatePresence>
        {confirmOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card border border-destructive/50 rounded-2xl p-6 max-w-sm w-full space-y-6"
            >
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-destructive" />
                </div>
                <h2 className="text-xl font-bold text-foreground mb-2">
                  ¿Activar SOS?
                </h2>
                <p className="text-sm text-muted-foreground">
                  Se activará el protocolo de emergencia con asistente de primeros auxilios por IA y se compartirá tu ubicación.
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={() => {
                    setConfirmOpen(false);
                    onTrigger();
                  }}
                  className="w-full h-14 bg-destructive hover:bg-destructive/90 text-destructive-foreground text-lg font-bold"
                >
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  ACTIVAR SOS
                </Button>

                <a
                  href="tel:112"
                  className="flex items-center justify-center gap-2 w-full h-12 rounded-lg border border-border bg-secondary/50 text-foreground font-mono text-sm hover:bg-secondary transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  Llamar al 112 directamente
                </a>

                <Button
                  variant="ghost"
                  onClick={() => setConfirmOpen(false)}
                  className="w-full text-muted-foreground"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancelar
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
